import React, { useState } from 'react';
import { 
  Youtube, Twitter, Instagram, Music2, BarChart3, Image, 
  Link2, X, Plus, Code, Play, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const RichMediaToolbar = ({ onInsert, contentRef }) => {
  const [activeModal, setActiveModal] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Chart state
  const [chartData, setChartData] = useState({
    type: 'bar',
    title: '',
    labels: '',
    values: '',
    colors: '#007AFF,#DC2626,#10B981,#F59E0B,#8B5CF6'
  });

  const tools = [
    { id: 'youtube', icon: Youtube, label: 'YouTube', color: 'bg-red-600 hover:bg-red-700' },
    { id: 'twitter', icon: Twitter, label: 'X Post', color: 'bg-black hover:bg-gray-800' },
    { id: 'instagram', icon: Instagram, label: 'Instagram', color: 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 hover:opacity-90' },
    { id: 'tiktok', icon: TikTokIcon, label: 'TikTok', color: 'bg-black hover:bg-gray-800' },
    { id: 'chart', icon: BarChart3, label: 'Chart', color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'gallery', icon: Image, label: 'Gallery', color: 'bg-emerald-600 hover:bg-emerald-700' },
  ];

  const generateYouTubeEmbed = (url) => {
    // Extract video ID from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        const videoId = match[1];
        return `
<div class="youtube-embed" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 20px 0; border-radius: 12px;">
  <iframe 
    src="https://www.youtube.com/embed/${videoId}" 
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 12px;"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
    allowfullscreen>
  </iframe>
</div>`;
      }
    }
    return null;
  };

  const generateTwitterEmbed = (url) => {
    // Extract tweet ID from Twitter/X URL
    const match = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
    if (match) {
      const tweetId = match[1];
      return `
<div class="twitter-embed" style="margin: 20px 0;">
  <blockquote class="twitter-tweet" data-theme="dark">
    <a href="${url}">Loading tweet...</a>
  </blockquote>
  <script async src="https://platform.twitter.com/widgets.js" charset="utf-8"></script>
</div>`;
    }
    return null;
  };

  const generateInstagramEmbed = (url) => {
    // Extract Instagram post ID
    const match = url.match(/instagram\.com\/(?:p|reel)\/([^\/\?]+)/);
    if (match) {
      const postId = match[1];
      return `
<div class="instagram-embed" style="margin: 20px auto; max-width: 540px;">
  <blockquote class="instagram-media" data-instgrm-captioned data-instgrm-permalink="${url}" data-instgrm-version="14" style="background:#FFF; border:0; border-radius:12px; margin: 0 auto; padding:0; width:100%;">
    <a href="${url}" target="_blank">View this post on Instagram</a>
  </blockquote>
  <script async src="//www.instagram.com/embed.js"></script>
</div>`;
    }
    return null;
  };

  const generateTikTokEmbed = (url) => {
    // Extract TikTok video ID
    const match = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
    if (match) {
      const videoId = match[1];
      return `
<div class="tiktok-embed" style="margin: 20px auto; max-width: 605px;">
  <blockquote class="tiktok-embed" cite="${url}" data-video-id="${videoId}" style="max-width: 605px; min-width: 325px;">
    <section><a target="_blank" href="${url}">View on TikTok</a></section>
  </blockquote>
  <script async src="https://www.tiktok.com/embed.js"></script>
</div>`;
    }
    return null;
  };

  const generateChartEmbed = () => {
    const labels = chartData.labels.split(',').map(l => l.trim()).filter(Boolean);
    const values = chartData.values.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
    const colors = chartData.colors.split(',').map(c => c.trim());
    
    if (labels.length === 0 || values.length === 0) {
      toast.error('Please enter labels and values for the chart');
      return null;
    }

    const chartConfig = {
      type: chartData.type,
      data: {
        labels,
        datasets: [{
          label: chartData.title || 'Data',
          data: values,
          backgroundColor: colors,
          borderColor: colors.map(c => c),
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: chartData.type === 'pie' || chartData.type === 'doughnut' },
          title: { display: !!chartData.title, text: chartData.title }
        }
      }
    };

    const chartUrl = `https://quickchart.io/chart?c=${encodeURIComponent(JSON.stringify(chartConfig))}&backgroundColor=transparent`;

    return `
<div class="chart-embed" style="margin: 20px 0; text-align: center;">
  ${chartData.title ? `<h4 style="color: #1a1a2e; font-weight: bold; margin-bottom: 10px;">${chartData.title}</h4>` : ''}
  <img src="${chartUrl}" alt="${chartData.title || 'Chart'}" style="max-width: 100%; height: auto; border-radius: 8px;" />
</div>`;
  };

  const generateGalleryEmbed = (urls) => {
    const imageUrls = urls.split('\n').map(u => u.trim()).filter(Boolean);
    if (imageUrls.length === 0) {
      toast.error('Please enter at least one image URL');
      return null;
    }

    const images = imageUrls.map((url, i) => `
      <div style="break-inside: avoid; margin-bottom: 16px;">
        <img src="${url}" alt="Gallery image ${i + 1}" style="width: 100%; border-radius: 8px; display: block;" loading="lazy" />
      </div>`).join('');

    return `
<div class="image-gallery" style="margin: 20px 0;">
  <div style="column-count: 2; column-gap: 16px;">
    ${images}
  </div>
</div>`;
  };

  const handleInsert = () => {
    let embed = null;
    
    switch (activeModal) {
      case 'youtube':
        embed = generateYouTubeEmbed(inputValue);
        if (!embed) {
          toast.error('Invalid YouTube URL. Please use a valid YouTube video link.');
          return;
        }
        break;
      case 'twitter':
        embed = generateTwitterEmbed(inputValue);
        if (!embed) {
          toast.error('Invalid X/Twitter URL. Please use a valid tweet link.');
          return;
        }
        break;
      case 'instagram':
        embed = generateInstagramEmbed(inputValue);
        if (!embed) {
          toast.error('Invalid Instagram URL. Please use a valid post or reel link.');
          return;
        }
        break;
      case 'tiktok':
        embed = generateTikTokEmbed(inputValue);
        if (!embed) {
          toast.error('Invalid TikTok URL. Please use a valid video link.');
          return;
        }
        break;
      case 'chart':
        embed = generateChartEmbed();
        if (!embed) return;
        break;
      case 'gallery':
        embed = generateGalleryEmbed(inputValue);
        if (!embed) return;
        break;
      default:
        return;
    }

    onInsert(embed);
    setActiveModal(null);
    setInputValue('');
    setChartData({ type: 'bar', title: '', labels: '', values: '', colors: '#007AFF,#DC2626,#10B981,#F59E0B,#8B5CF6' });
    toast.success('Media embedded successfully!');
  };

  const renderModal = () => {
    if (!activeModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setActiveModal(null)}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="bg-slate-100 px-6 py-4 flex items-center justify-between border-b">
            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
              {activeModal === 'youtube' && <><Youtube className="w-5 h-5 text-red-600" /> Embed YouTube Video</>}
              {activeModal === 'twitter' && <><Twitter className="w-5 h-5" /> Embed X Post</>}
              {activeModal === 'instagram' && <><Instagram className="w-5 h-5 text-pink-600" /> Embed Instagram</>}
              {activeModal === 'tiktok' && <><TikTokIcon className="w-5 h-5" /> Embed TikTok</>}
              {activeModal === 'chart' && <><BarChart3 className="w-5 h-5 text-blue-600" /> Create Chart</>}
              {activeModal === 'gallery' && <><Image className="w-5 h-5 text-emerald-600" /> Image Gallery</>}
            </h3>
            <button onClick={() => setActiveModal(null)} className="text-slate-500 hover:text-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6 space-y-4">
            {activeModal === 'chart' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Chart Type</label>
                  <select
                    value={chartData.type}
                    onChange={e => setChartData({ ...chartData, type: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="bar">Bar Chart</option>
                    <option value="line">Line Chart</option>
                    <option value="pie">Pie Chart</option>
                    <option value="doughnut">Doughnut Chart</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Chart Title</label>
                  <input
                    type="text"
                    value={chartData.title}
                    onChange={e => setChartData({ ...chartData, title: e.target.value })}
                    placeholder="e.g., SWAC Passing Leaders"
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Labels (comma separated)</label>
                  <input
                    type="text"
                    value={chartData.labels}
                    onChange={e => setChartData({ ...chartData, labels: e.target.value })}
                    placeholder="e.g., Jackson State, Grambling, Southern"
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Values (comma separated)</label>
                  <input
                    type="text"
                    value={chartData.values}
                    onChange={e => setChartData({ ...chartData, values: e.target.value })}
                    placeholder="e.g., 350, 280, 320"
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            ) : activeModal === 'gallery' ? (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Image URLs (one per line)</label>
                <textarea
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 h-32 font-mono text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">Enter image URLs, one per line. They'll display in a 2-column masonry layout.</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {activeModal === 'youtube' && 'YouTube Video URL'}
                  {activeModal === 'twitter' && 'X/Twitter Post URL'}
                  {activeModal === 'instagram' && 'Instagram Post/Reel URL'}
                  {activeModal === 'tiktok' && 'TikTok Video URL'}
                </label>
                <input
                  type="url"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  placeholder={
                    activeModal === 'youtube' ? 'https://youtube.com/watch?v=...' :
                    activeModal === 'twitter' ? 'https://x.com/user/status/...' :
                    activeModal === 'instagram' ? 'https://instagram.com/p/...' :
                    'https://tiktok.com/@user/video/...'
                  }
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t">
            <Button variant="ghost" onClick={() => setActiveModal(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInsert}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Insert
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 overflow-hidden mb-2">
        {/* Toolbar Header */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-indigo-100/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Code className="w-5 h-5 text-indigo-600" />
            <span className="font-bold text-slate-800">Rich Media Builder</span>
            <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full">YouTube, X, Instagram, TikTok, Charts</span>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
        </button>

        {/* Toolbar Buttons */}
        {isExpanded && (
          <div className="px-4 pb-4">
            <div className="flex flex-wrap gap-2">
              {tools.map(tool => (
                <button
                  key={tool.id}
                  type="button"
                  onClick={() => setActiveModal(tool.id)}
                  className={`${tool.color} text-white px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all hover:scale-105 shadow-sm`}
                  data-testid={`embed-${tool.id}-btn`}
                >
                  <tool.icon className="w-4 h-4" />
                  {tool.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Click a button above to embed media directly into your article content.
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {renderModal()}
    </>
  );
};

export default RichMediaToolbar;
